import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { 
  ArrowRight, Truck, Shield, CreditCard, Headphones, ChevronLeft, ChevronRight, 
  Car, Plane, UtensilsCrossed, ShoppingBasket, Zap, Clock, Star, Heart,
  Smartphone, Shirt, Home, Dumbbell, Sparkles, Baby, Gift, Percent,
  ChefHat, Flower2, Mountain, Sofa, Wrench, CircleDot, Building2, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ProductCard } from '@/components/products/ProductCard';
import { ProductThumbnail } from '@/components/products/ProductThumbnail';
import { Skeleton } from '@/components/ui/skeleton';
import { products, categories as categoriesApi } from '@/lib/api';
import { useCurrency } from '@/lib/currency-context';
import { useWishlist } from '@/lib/wishlist-context';
import { useAuth } from '@/lib/auth-context';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getPublicSiteUrl } from '@/lib/seo';
import { canonicalHomeUrl, searchActionTargetUrl, getLocaleFromPathname, buildPathForLocale, localizeInternalPath } from '@/lib/intl-seo';
import { buildProductPath } from '@/lib/product-url';
import { homeOrganizationAndWebsiteJsonLd, jsonLdScript } from '@/lib/schema-org';
import { renderSocialMetaTags } from '@/components/seo/SocialMetaTags';
import { isHomePageItemEnabled, sortQuickStripDisplayOrder, HOMEPAGE_REFRESH_EVENT } from '@/lib/homePageVisibility';
import { shouldBlurSensitiveImage, withoutSensitiveProducts } from '@/lib/sensitive-content';

const HOME_IMAGE_SKIP = ['placeholder', 'no-image', 'noimage', 'no_image', 'blank.gif'];

function homeProductImage(product) {
  const images = product?.images;
  const list = Array.isArray(images) ? images : (typeof images === 'string' ? [images] : []);
  for (const img of list) {
    const url = (typeof img === 'string' ? img : (img?.url || img?.src || '')).trim();
    if (url.length < 4) continue;
    const lower = url.toLowerCase();
    if (['null', 'none', 'undefined', 'n/a', 'false'].includes(lower)) continue;
    if (HOME_IMAGE_SKIP.some((bad) => lower.includes(bad))) continue;
    if (lower.startsWith('http://') || lower.startsWith('https://') || lower.startsWith('/') || lower.startsWith('uploads/')) {
      return url;
    }
  }
  return '';
}

function withHomepageImages(list) {
  return withoutSensitiveProducts(list).map((product) => {
    const image = homeProductImage(product);
    if (!image) return null;
    const images = Array.isArray(product.images) ? product.images : [];
    const rest = images.filter((img) => {
      const url = typeof img === 'string' ? img.trim() : '';
      return url && url !== image && homeProductImage({ images: [url] });
    });
    return { ...product, images: [image, ...rest].slice(0, 2) };
  }).filter(Boolean);
}

function spreadHomeProducts(list, count, used) {
  const buckets = new Map();
  for (const product of withHomepageImages(list || [])) {
    if (!product?.id || used.has(product.id)) continue;
    const image = homeProductImage(product);
    if (!image || used.has(`img:${image}`)) continue;
    const key = product.category || 'Genel';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(product);
  }
  const keys = [...buckets.keys()];
  const rows = [];
  let cycle = 0;
  while (rows.length < count && keys.length) {
    let added = false;
    for (const key of keys) {
      const product = buckets.get(key)[cycle];
      if (!product) continue;
      used.add(product.id);
      const image = homeProductImage(product);
      if (image) used.add(`img:${image}`);
      rows.push(product);
      added = true;
      if (rows.length >= count) break;
    }
    if (!added) break;
    cycle += 1;
  }
  return rows;
}

const HOME_MIN_CLOTHING = 5;
const HOME_MIN_SHOES = 5;

function foldTr(value) {
  return String(value || '').trim().toLocaleLowerCase('tr-TR');
}

const HOME_SHOE_CATEGORIES = new Set([
  'ayakkabı', 'spor ayakkabı', 'topuklu ayakkabı', 'babet ayakkabı', 'bot',
]);
const HOME_SHOE_SUBCATEGORIES = new Set([
  'ayakkabı', '100 deri ayakkabı', 'erkek ayakkabı', 'kadın ayakkabı',
  'erkek bot', 'kadın spor ayakkabı', 'çocuk ayakkabı',
]);
const HOME_NOT_SHOE_NAME = /ayakkab[ıi]l[ıi]k|organizer|d[uü]zenleyici|y[ıi]kama|kutu|tabanl[ıi]k|bak[ıi]m|patik|filesi|[cç]orap|terlik/i;
const HOME_CLOTHING_SUB_BLOCK = /ayakkab|fantezi|aksesuar|gecelik|patik|[cç]anta|(^|\s)bot$|i[cç]\s*giyim/i;
const HOME_NOT_CLOTHING_NAME = /klips|toka(?:\s|$)|tokas[ıi]|e[sş]arp|(?:^|\s)[sş]al(?:\s|$)|bro[sş]|kemer|[cç]anta|c[uü]zdan|g[oö]zl[uü]k|k[uü]pe|kolye|bileklik|y[uü]z[uü]k|gecelik|fantezi|i[cç]\s*giyim/i;

function isHomeShoe(product) {
  const blob = `${foldTr(product?.name)} ${foldTr(product?.name_tr)} ${foldTr(product?.subcategory)}`;
  if (HOME_NOT_SHOE_NAME.test(blob)) return false;
  if (HOME_SHOE_CATEGORIES.has(foldTr(product?.category))) return true;
  return HOME_SHOE_SUBCATEGORIES.has(foldTr(product?.subcategory));
}

function isHomeClothing(product) {
  if (!product || isHomeShoe(product)) return false;
  const category = foldTr(product.category);
  if (!category.includes('giyim')) return false;
  if (/fantezi|plaj|i[cç]\s*giyim/.test(category)) return false;
  const subcategory = foldTr(product.subcategory);
  if (subcategory && HOME_CLOTHING_SUB_BLOCK.test(subcategory)) return false;
  const blob = `${foldTr(product.name)} ${foldTr(product.name_tr)}`;
  if (HOME_NOT_CLOTHING_NAME.test(blob)) return false;
  return true;
}

function rememberHomeProduct(used, product) {
  if (!product?.id) return;
  used.add(product.id);
  const image = homeProductImage(product);
  if (image) used.add(`img:${image}`);
}

function takeHomeQuota(list, used, predicate, count) {
  const picked = [];
  if (count <= 0) return picked;
  for (const product of list || []) {
    if (picked.length >= count) break;
    if (!product?.id || used.has(product.id)) continue;
    const image = homeProductImage(product);
    if (!image || used.has(`img:${image}`)) continue;
    if (!predicate(product)) continue;
    rememberHomeProduct(used, product);
    picked.push(product);
  }
  return picked;
}

function weaveHomeRows(reserved, rest, count) {
  const rows = [];
  let reservedIndex = 0;
  let restIndex = 0;
  const reservedRows = reserved || [];
  const restRows = rest || [];
  while (rows.length < count && (reservedIndex < reservedRows.length || restIndex < restRows.length)) {
    const useReserved = reservedIndex < reservedRows.length && (rows.length % 3 === 0 || restIndex >= restRows.length);
    if (useReserved) rows.push(reservedRows[reservedIndex++]);
    else rows.push(restRows[restIndex++]);
  }
  return rows;
}

const HomePage = () => {
  const { lang } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const site = getPublicSiteUrl();
  const langCode =
    lang || getLocaleFromPathname(location.pathname) || (i18n.language || 'en').substring(0, 2);
  const { formatPrice } = useCurrency();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allProducts, setAllProducts] = useState([]);
  const [flashDealProducts, setFlashDealProducts] = useState([]);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const categoryScrollRef = useRef(null);
  const [countdown, setCountdown] = useState({ hours: 5, minutes: 23, seconds: 45 });
  const [homepageSettings, setHomepageSettings] = useState(null);
  const [topBannerIndex, setTopBannerIndex] = useState(0);
  const [topBannerVisible, setTopBannerVisible] = useState(true);
  const retryCountRef = useRef(0);
  const homeRequestRef = useRef(0);
  const [homeRefreshToken, setHomeRefreshToken] = useState(0);
  const [brokenImageIds, setBrokenImageIds] = useState(() => new Set());
  const maxRetries = 3;

  // Admin Yazı 1 / Yazı 2 — dil seçimine göre çeviri; YETERKİ SEN ÜRET TÜRKİYE sabit (i18n)
  const bannerLangTrans =
    langCode !== 'tr'
      ? homepageSettings?.top_banner_translations?.[langCode]
      : null;
  const topBannerTexts = [
    bannerLangTrans?.text || homepageSettings?.top_banner_text || t('home.superDeal'),
    bannerLangTrans?.subtext || homepageSettings?.top_banner_subtext,
  ].filter((text) => String(text || '').trim());

  useEffect(() => {
    if (topBannerTexts.length < 2) return undefined;
    let fadeTimer;
    const interval = setInterval(() => {
      setTopBannerVisible(false);
      fadeTimer = setTimeout(() => {
        setTopBannerIndex((i) => (i + 1) % topBannerTexts.length);
        setTopBannerVisible(true);
      }, 280);
    }, 4000);
    return () => {
      clearInterval(interval);
      if (fadeTimer) clearTimeout(fadeTimer);
    };
  }, [topBannerTexts.length, topBannerTexts[0], topBannerTexts[1]]);

  // Handle wishlist toggle for homepage products
  const handleWishlistToggle = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error(t('wishlist.loginRequired') || 'Favorilere eklemek için giriş yapmalısınız');
      navigate('/login');
      return;
    }
    
    await toggleWishlist(product.id, product.name);
  };

  useEffect(() => {
    const onHomeClick = () => setHomeRefreshToken((n) => n + 1);
    window.addEventListener(HOMEPAGE_REFRESH_EVENT, onHomeClick);
    const onPageShow = (event) => {
      if (event.persisted) setHomeRefreshToken((n) => n + 1);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener(HOMEPAGE_REFRESH_EVENT, onHomeClick);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  useEffect(() => {
    const requestId = homeRequestRef.current + 1;
    homeRequestRef.current = requestId;
    retryCountRef.current = 0;
    fetchHomePageData(requestId);
    return () => {
      homeRequestRef.current += 1;
    };
  }, [i18n.language, location.key, homeRefreshToken]);

  const fetchHomePageData = async (requestId = homeRequestRef.current) => {
    const lang = i18n.language;
    const stillCurrent = () => requestId === homeRequestRef.current;
    let scheduleRetry = false;
    try {
      if (!allProducts.length) setLoading(true);

      const samplePromise = api.get('/products/home-sample', {
        params: { limit: 96, lang, _: Date.now() },
        timeout: 8000,
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      }).catch(() => products.list({ limit: 200, lang, exclude_sensitive: true, _: Date.now() }));

      const [categoriesRes, allProductsRes, settingsRes] = await Promise.allSettled([
        categoriesApi.list(),
        samplePromise,
        api.get('/admin/homepage/settings', { timeout: 4000 }),
      ]);

      if (!stillCurrent()) return;

      const categoriesData =
        categoriesRes.status === 'fulfilled' ? categoriesRes.value.data || [] : [];
      const allData =
        allProductsRes.status === 'fulfilled' ? allProductsRes.value.data?.products || [] : [];
      const settings = settingsRes.status === 'fulfilled' ? settingsRes.value.data : null;

      if (categoriesData.length > 0) setCategories(categoriesData);
      const visibleProducts = withHomepageImages(allData);
      if (visibleProducts.length > 0) {
        setBrokenImageIds(new Set());
        setAllProducts(visibleProducts);
      }
      if (settings) setHomepageSettings(settings);

      const hasAnyProducts = visibleProducts.length > 0;
      if (!hasAnyProducts && retryCountRef.current < maxRetries) {
        retryCountRef.current += 1;
        scheduleRetry = true;
        setTimeout(() => fetchHomePageData(requestId), retryCountRef.current * 2000);
        return;
      }
      retryCountRef.current = 0;

      const flashManual = settings?.flash_deals_auto_select === false && settings?.flash_deals_products?.length > 0;
      const recManual = settings?.recommended_auto_select === false && settings?.recommended_products?.length > 0;
      if (flashManual || recManual) {
        const [flashRes, recRes] = await Promise.allSettled([
          flashManual
            ? products.list({ ids: settings.flash_deals_products.join(','), limit: 32, lang, exclude_sensitive: true })
            : Promise.resolve(null),
          recManual
            ? products.list({ ids: settings.recommended_products.join(','), limit: 32, lang, exclude_sensitive: true })
            : Promise.resolve(null),
        ]);
        const flashRows = flashRes.status === 'fulfilled' ? flashRes.value?.data?.products || [] : [];
        const recRows = recRes.status === 'fulfilled' ? recRes.value?.data?.products || [] : [];
        if (!stillCurrent()) return;
        const visibleFlash = withHomepageImages(flashRows);
        const visibleRec = withHomepageImages(recRows);
        if (!stillCurrent()) return;
        if (visibleFlash.length > 0) setFlashDealProducts(visibleFlash);
        if (visibleRec.length > 0) setRecommendedProducts(visibleRec);
      }

      if (stillCurrent()) setLoading(false);
    } catch (error) {
      console.error('Failed to fetch homepage data:', error);
      if (!stillCurrent()) return;
      if (retryCountRef.current < maxRetries) {
        retryCountRef.current += 1;
        scheduleRetry = true;
        setTimeout(() => fetchHomePageData(requestId), retryCountRef.current * 2000);
      } else {
        setLoading(false);
      }
    } finally {
      if (scheduleRetry) {
        /* loading true kalır, retry devam eder */
      }
    }
  };


  // Countdown timer
  useEffect(() => {
    if (!homepageSettings?.flash_deals_countdown_enabled) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return { hours: 23, minutes: 59, seconds: 59 };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [homepageSettings?.flash_deals_countdown_enabled]);

  // Quick category data with icons and images - Updated with actual product categories
  const quickCategories = [
    // Service Categories - At the beginning
    { 
      id: 'turk-restoranlari', 
      nameKey: 'home.services.restaurants',
      icon: ChefHat, 
      color: 'bg-red-600',
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=200&h=200&fit=crop',
      link: '/restaurants',
      isService: true
    },
    { 
      id: 'ucak-bileti', 
      nameKey: 'home.services.flights',
      icon: Plane, 
      color: 'bg-sky-600',
      image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=200&h=200&fit=crop',
      link: '/flights',
      isService: true
    },
    { 
      id: 'otel-rezervasyon', 
      nameKey: 'home.services.hotels',
      icon: Building2, 
      color: 'bg-indigo-600',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=200&h=200&fit=crop',
      link: '/hotels',
      isService: true
    },
    { 
      id: 'taxi-limuzin', 
      nameKey: 'home.services.taxiLimo',
      icon: Car, 
      color: 'bg-amber-600',
      image: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=200&h=200&fit=crop',
      link: '/taxi-limousine',
      isService: true
    },
    {
      id: 'aktar',
      nameKey: 'home.services.transfer',
      icon: MapPin,
      color: 'bg-teal-600',
      image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=200&h=200&fit=crop',
      categorySlug: 'aktar',
      isService: true
    },
    { 
      id: 'turk-marketleri', 
      nameKey: 'home.services.markets',
      icon: ShoppingBasket, 
      color: 'bg-green-600',
      image: 'https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=200&h=200&fit=crop',
      link: '/supermarkets',
      isService: true
    },
    { 
      id: 'diger-hizmetler', 
      nameKey: 'home.services.other',
      icon: Gift, 
      color: 'bg-purple-600',
      image: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=200&h=200&fit=crop',
      link: '/products',
      isService: true
    },
    // Product Categories
    { 
      id: 'mutfak', 
      nameKey: 'categories.mutfak',
      icon: ChefHat, 
      color: 'bg-red-500',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=200&h=200&fit=crop',
      categorySlug: 'mutfak',
    },
    { 
      id: 'yatak-odasi', 
      nameKey: 'categories.yatakOdasi',
      icon: Home, 
      color: 'bg-indigo-500',
      image: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?w=200&h=200&fit=crop',
      categorySlug: 'yatak-odasi',
    },
    { 
      id: 'giyim-moda', 
      nameKey: 'categories.giyimModa',
      icon: Shirt, 
      color: 'bg-purple-500',
      image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=200&h=200&fit=crop',
      categorySlug: 'kadin-giyim',
    },
    { 
      id: 'kadin-giyim', 
      nameKey: 'categories.kadinGiyim',
      icon: Shirt, 
      color: 'bg-rose-500',
      image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=200&h=200&fit=crop',
      categorySlug: 'kadin-giyim',
    },
    { 
      id: 'spor', 
      nameKey: 'categories.spor',
      icon: Dumbbell, 
      color: 'bg-green-500',
      image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=200&h=200&fit=crop',
      categorySlug: 'spor',
    },
    { 
      id: 'ev-yasam', 
      nameKey: 'categories.evYasam',
      icon: Sofa, 
      color: 'bg-cyan-500',
      image: 'https://images.unsplash.com/photo-1556909114-44e3e70034e2?w=200&h=200&fit=crop',
      categorySlug: 'ev-&-yasam',
    },
    { 
      id: 'aksesuar', 
      nameKey: 'categories.aksesuar',
      icon: Sparkles, 
      color: 'bg-orange-500',
      image: 'https://images.unsplash.com/photo-1562273138-f46be4ebdf33?w=200&h=200&fit=crop',
      categorySlug: 'aksesuar',
    },
    { 
      id: 'bijuteri', 
      nameKey: 'bijuteri.title',
      icon: Sparkles, 
      color: 'bg-pink-500',
      image: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=200&h=200&fit=crop',
      link: null, // Will use dynamic language prefix
      isHighlighted: true,
      isDynamic: true
    },
    { 
      id: 'bahce', 
      nameKey: 'categories.bahce',
      icon: Flower2, 
      color: 'bg-emerald-600',
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=200&h=200&fit=crop',
      categorySlug: 'bahce',
    },
    { 
      id: 'kozmetik', 
      nameKey: 'categories.kozmetik',
      icon: Sparkles, 
      color: 'bg-pink-400',
      image: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=200&h=200&fit=crop',
      categorySlug: 'kozmetik',
    },
    { 
      id: 'hediyelik', 
      nameKey: 'categories.hediyelik',
      icon: Gift, 
      color: 'bg-amber-500',
      image: 'https://images.unsplash.com/photo-1513885535751-8b9238bd345a?w=200&h=200&fit=crop',
      categorySlug: 'hediyelik',
    },
    { 
      id: 'elektronik', 
      nameKey: 'categories.elektronik',
      icon: Smartphone, 
      color: 'bg-blue-600',
      image: 'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=200&h=200&fit=crop',
      categorySlug: 'elektronik',
    },
    { 
      id: 'bebek', 
      nameKey: 'categories.bebek',
      icon: Baby, 
      color: 'bg-sky-400',
      image: 'https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=200&h=200&fit=crop',
      categorySlug: 'bebek',
    },
    { 
      id: 'oto-aksesuar', 
      nameKey: 'categories.otoAksesuar',
      icon: Car, 
      color: 'bg-gray-700',
      image: 'https://images.unsplash.com/photo-1489824904134-891ab64532f1?w=200&h=200&fit=crop',
      categorySlug: 'oto-aksesuar',
    },
    {
      id: 'oto-lastik',
      nameKey: 'categories.otoLastik',
      icon: CircleDot,
      color: 'bg-slate-800',
      image: 'https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=200&h=200&fit=crop',
      link: '/products?source=lastikcim',
    },
  ];

  // Admin «Kategoriler» sekmesindeki görsel/link/renk → üst şerit (id eşleşmesi, örn. aktar)
  const categoryCardById = (() => {
    const map = new Map();
    (homepageSettings?.categories || []).forEach((c) => {
      if (c?.id) map.set(c.id, c);
    });
    return map;
  })();

  const quickCategoriesResolved = quickCategories.map((cat) => {
    const override = categoryCardById.get(cat.id);
    if (!override) return cat;
    const next = {
      ...cat,
      image: override.image || cat.image,
      color: override.color || cat.color,
    };
    // Admin linki: /category/aktar → categorySlug; diğer linkler → link
    if (override.link) {
      const m = String(override.link).match(/\/category\/([^/?#]+)/i);
      if (m) {
        next.categorySlug = decodeURIComponent(m[1]);
        delete next.link;
      } else {
        next.link = override.link;
      }
    }
    return next;
  });

  const displayQuickCategories = sortQuickStripDisplayOrder(
    quickCategoriesResolved.filter((c) =>
      isHomePageItemEnabled(homepageSettings?.home_quick_categories, c.id)
    ),
    homepageSettings?.home_quick_categories
  );

  const homeServicesGridItems = [
    {
      id: 'svc-supermarkets',
      name: t('services.turkishSupermarkets'),
      icon: ShoppingBasket,
      link: '/supermarkets',
      color: 'from-emerald-500 to-emerald-600',
      desc: t('services.turkishSupermarketsDesc'),
      image: 'https://images.unsplash.com/photo-1741010812421-2b5bff9f95fc?w=300&h=200&fit=crop&q=70',
    },
    {
      id: 'svc-restaurants',
      name: t('services.turkishRestaurants'),
      icon: UtensilsCrossed,
      link: '/restaurants',
      color: 'from-orange-500 to-red-500',
      desc: t('services.turkishRestaurantsDesc'),
      image: 'https://images.pexels.com/photos/5779788/pexels-photo-5779788.jpeg?w=300&h=200&fit=crop',
    },
    {
      id: 'svc-car-rental',
      name: t('services.carRental'),
      icon: Car,
      link: '/car-rentals',
      color: 'from-blue-500 to-blue-600',
      desc: t('services.carRentalDesc'),
      image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=300&h=200&fit=crop&q=70',
    },
    {
      id: 'svc-taxi',
      name: t('services.taxiLimousine'),
      icon: Car,
      link: '/taxi-limousine',
      color: 'from-yellow-500 to-amber-600',
      desc: t('services.taxiLimousineDesc'),
      image: 'https://images.unsplash.com/photo-1511527661048-7fe73d85e9a4?w=300&h=200&fit=crop&q=70',
    },
    {
      id: 'svc-flights',
      name: t('services.flights'),
      icon: Plane,
      link: '/flights',
      color: 'from-sky-500 to-sky-600',
      desc: t('services.flightsDesc'),
      image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=300&h=200&fit=crop&q=70',
    },
    {
      id: 'svc-hotels',
      name: t('services.hotels'),
      icon: Building2,
      link: '/hotels',
      color: 'from-indigo-500 to-purple-600',
      desc: t('services.hotelsDesc'),
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=300&h=200&fit=crop&q=70',
    },
    {
      id: 'svc-auto-parts',
      name: t('services.autoParts'),
      icon: Wrench,
      categorySlug: 'oto-aksesuar',
      color: 'from-gray-600 to-gray-700',
      desc: t('services.autoPartsDesc'),
      image: 'https://images.unsplash.com/photo-1489824904134-891ab64532f1?w=300&h=200&fit=crop',
    },
    {
      id: 'svc-tires',
      name: t('services.tires'),
      icon: CircleDot,
      link: '/tire-appointment',
      color: 'from-slate-700 to-slate-800',
      desc: t('services.tiresDesc'),
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=200&fit=crop&q=70',
    },
  ].filter((s) => isHomePageItemEnabled(homepageSettings?.home_services_items, s.id));

  const scrollCategories = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 200;
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const hideBrokenProduct = (id) => {
    if (!id) return;
    setBrokenImageIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const usedHomeIds = new Set();
  const visiblePool = allProducts.filter((product) => product?.id && !brokenImageIds.has(product.id));
  const flashSource = withHomepageImages(flashDealProducts).filter((product) => !brokenImageIds.has(product.id));
  const recommendedSource = withHomepageImages(recommendedProducts).filter((product) => !brokenImageIds.has(product.id));
  const flashManual = flashSource.length > 0;
  const recommendedManual = recommendedSource.length > 0;
  const pinnedRows = [
    ...(flashManual ? flashSource.slice(0, 32) : []),
    ...(recommendedManual ? recommendedSource.slice(0, 32) : []),
  ];
  pinnedRows.forEach((product) => rememberHomeProduct(usedHomeIds, product));

  const clothingPick = takeHomeQuota(
    visiblePool,
    usedHomeIds,
    isHomeClothing,
    Math.max(0, HOME_MIN_CLOTHING - pinnedRows.filter(isHomeClothing).length),
  );
  const shoePick = takeHomeQuota(
    visiblePool,
    usedHomeIds,
    isHomeShoe,
    Math.max(0, HOME_MIN_SHOES - pinnedRows.filter(isHomeShoe).length),
  );
  let flashQuota = [];
  let recommendedQuota = [];
  clothingPick.forEach((product, index) => {
    (index < 3 ? flashQuota : recommendedQuota).push(product);
  });
  shoePick.forEach((product, index) => {
    (index < 2 ? flashQuota : recommendedQuota).push(product);
  });
  if (flashManual && recommendedManual) {
    recommendedQuota = recommendedQuota.concat(flashQuota);
    flashQuota = [];
  } else if (flashManual) {
    recommendedQuota = recommendedQuota.concat(flashQuota);
    flashQuota = [];
  } else if (recommendedManual) {
    flashQuota = flashQuota.concat(recommendedQuota);
    recommendedQuota = [];
  }

  const flashRows = flashManual
    ? (flashQuota.length ? weaveHomeRows(flashQuota, flashSource.slice(0, 32), 32) : flashSource.slice(0, 32))
    : weaveHomeRows(flashQuota, spreadHomeProducts(visiblePool, Math.max(0, 32 - flashQuota.length), usedHomeIds), 32);
  const recommendedRows = recommendedManual
    ? (recommendedQuota.length ? weaveHomeRows(recommendedQuota, recommendedSource.slice(0, 32), 32) : recommendedSource.slice(0, 32))
    : weaveHomeRows(recommendedQuota, spreadHomeProducts(visiblePool, Math.max(0, 32 - recommendedQuota.length), usedHomeIds), 32);

  const homeTitle = t('seo.homeTitle', 'Turkish E-Market | Türk Ürünleri Online Pazaryeri');
  const homeCanonical = site ? canonicalHomeUrl(site, langCode) : '';
  const homeOgImage = site ? `${site}/logo.png` : '';
  const homeDesc =
    t(
      'seo.homeDescription',
      'Turkish E-Market - Türk gıda ürünleri, süpermarket, restoran ve daha fazlası için online pazaryeri',
    );
  const homeGraphJsonLd = site
    ? jsonLdScript(
        homeOrganizationAndWebsiteJsonLd({
          site,
          langCode,
          description: homeDesc,
          searchTarget: searchActionTargetUrl(site, langCode),
        }),
      )
    : '';

  return (
    <div data-testid="home-page" className="bg-gray-50" data-seo-ready={!loading ? 'true' : undefined}>
      <h1 className="sr-only">{t('seo.homeH1')}</h1>
      <Helmet htmlAttributes={{ lang: langCode }}>
        <title>{homeTitle}</title>
        <meta name="description" content={homeDesc} />
        {homeCanonical ? <link rel="canonical" href={homeCanonical} /> : null}
        {renderSocialMetaTags({
          title: homeTitle,
          description: homeDesc,
          url: homeCanonical,
          image: homeOgImage,
          type: 'website',
          locale: langCode,
        })}
        {homeGraphJsonLd ? (
          <script type="application/ld+json">{homeGraphJsonLd}</script>
        ) : null}
      </Helmet>
      {/* Top Campaign Banner — dönen slogan (admin) + sabit YETERKİ SEN ÜRET TÜRKİYE */}
      {homepageSettings?.top_banner_enabled !== false && (
        <div
          className={`bg-gradient-to-r ${homepageSettings?.top_banner_gradient || 'from-red-600 via-red-500 to-orange-500'} text-white py-1 sm:py-2 overflow-hidden`}
          data-testid="top-campaign-banner"
        >
          <div className="container mx-auto px-2 sm:px-4">
            {/* Mobile */}
            <div className="sm:hidden flex items-center justify-center gap-1.5 whitespace-nowrap text-[10px] font-medium min-h-[1.1rem]">
              <span
                className={`font-semibold transition-opacity duration-300 ${
                  topBannerVisible ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {topBannerTexts[topBannerIndex % Math.max(topBannerTexts.length, 1)] || t('home.superDeal')}
              </span>
              <span>•</span>
              <span>{t('home.freeShippingOver')}</span>
            </div>
            {/* Desktop */}
            <div className="hidden sm:flex items-center justify-center gap-3 text-sm">
              <Zap className="w-4 h-4 flex-shrink-0" />
              <span
                className={`font-semibold transition-opacity duration-300 ${
                  topBannerVisible ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {topBannerTexts[topBannerIndex % Math.max(topBannerTexts.length, 1)] || t('home.superDeal')}
              </span>
              <span className="text-white/90">{t('home.freeShippingOver')}</span>
              {(() => {
                const rawLink = homepageSettings?.top_banner_link || '/products?featured=true';
                const linkHref = rawLink.startsWith('http')
                  ? rawLink
                  : localizeInternalPath(rawLink.startsWith('/') ? rawLink : `/${rawLink}`, langCode);
                const linkLabel =
                  bannerLangTrans?.link_text ||
                  homepageSettings?.top_banner_link_text ||
                  t('home.shopNow');
                return (
                  <Link to={linkHref} className="underline hover:no-underline font-medium">
                    {linkLabel} →
                  </Link>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Quick Category Navigation - Compact with Images */}
      {homepageSettings?.quick_category_strip_enabled !== false && displayQuickCategories.length > 0 ? (
      <section className="py-3 bg-white border-b">
        <div className="container mx-auto px-4 relative group/slider">
          {/* Left Arrow - Desktop only */}
          <button
            onClick={() => scrollCategories('left')}
            className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center bg-white/90 hover:bg-white border shadow-md rounded-full opacity-0 group-hover/slider:opacity-100 transition-opacity"
            data-testid="cat-scroll-left"
          >
            <ChevronLeft className="w-4 h-4 text-gray-700" />
          </button>
          
          <div 
            ref={categoryScrollRef}
            className="flex items-center gap-3 overflow-x-auto pb-1 -mb-1 touch-pan-x hide-scrollbar scroll-smooth"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {displayQuickCategories.map((cat) => {
              // Add width parameter for optimization
              const optimizedCatImage = cat.image.includes('?') 
                ? cat.image + '&w=160&q=70' 
                : cat.image + '?w=160&q=70';
              // Generate link with language prefix for dynamic categories
              const catLink = cat.isDynamic
                ? buildPathForLocale(langCode, ['bijuteri'])
                : cat.categorySlug
                  ? buildPathForLocale(langCode, ['category', cat.categorySlug])
                  : localizeInternalPath(cat.link || '/products', langCode);
              return (
              <Link
                key={cat.id}
                to={catLink}
                className="flex-shrink-0 group"
                data-testid={`quick-cat-${cat.id}`}
              >
                <div className={`relative ${cat.isService ? 'w-24 h-24' : 'w-20 h-20'} rounded-xl overflow-hidden shadow-sm group-hover:shadow-md transition-all group-hover:-translate-y-0.5`}>
                  <img 
                    src={optimizedCatImage} 
                    alt={t(cat.nameKey)}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className={`absolute inset-0 ${cat.color} opacity-30 group-hover:opacity-20 transition-opacity`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-1.5">
                    <span className={`text-white ${cat.isService ? 'text-[9px] leading-tight' : 'text-[10px]'} font-semibold drop-shadow-lg ${cat.isService ? 'line-clamp-2' : 'line-clamp-1'}`}>{t(cat.nameKey)}</span>
                  </div>
                </div>
              </Link>
            );})}
          </div>
          
          {/* Right Arrow - Desktop only */}
          <button
            onClick={() => scrollCategories('right')}
            className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center bg-white/90 hover:bg-white border shadow-md rounded-full opacity-0 group-hover/slider:opacity-100 transition-opacity"
            data-testid="cat-scroll-right"
          >
            <ChevronRight className="w-4 h-4 text-gray-700" />
          </button>
        </div>
      </section>
      ) : null}

      {/* Flash Deals Hero Section */}
      <section className="bg-gray-100 py-4 sm:py-6">
        <div className="container mx-auto px-3 sm:px-4">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="bg-red-500 p-1.5 sm:p-2 rounded-lg">
                <Zap className="w-5 sm:w-6 h-5 sm:h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-2xl font-bold text-gray-800">
                  {t('home.flashDeals')}
                </h2>
                <p className="text-gray-600 text-xs sm:text-sm hidden sm:block">
                  {t('home.limitedTimeOffers')}
                </p>
              </div>
            </div>
            {/* Countdown Timer - Only show if enabled */}
            {homepageSettings?.flash_deals_countdown_enabled && (
              <div className="flex items-center gap-2 sm:gap-3 bg-red-500 px-2 sm:px-4 py-1.5 sm:py-2 rounded-full self-start sm:self-auto">
                <Clock className="w-3 sm:w-4 h-3 sm:h-4 text-white" />
                <div className="flex gap-0.5 sm:gap-1 text-white font-mono font-bold text-xs sm:text-base">
                  <span className="bg-red-600 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                    {String(countdown.hours).padStart(2, '0')}
                  </span>
                  <span className="py-0.5 sm:py-1">:</span>
                  <span className="bg-red-600 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                    {String(countdown.minutes).padStart(2, '0')}
                  </span>
                  <span className="py-0.5 sm:py-1">:</span>
                  <span className="bg-red-600 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                    {String(countdown.seconds).padStart(2, '0')}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Flash Deal Products Grid - Kaçırılmayacak Fırsatlar - 8x3 Grid (24 ürün) */}
          {loading ? (
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2 sm:gap-3">
              {[...Array(24)].map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-xl bg-gray-200" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2 sm:gap-3" data-testid="flash-deals">
              {flashRows.map((product, index) => {
                // Get translated name and description based on current language
                // Use only first 2 chars to handle cases like "en-US" -> "en"
                const currentLang = (i18n.language || 'tr').substring(0, 2);
                const translations = product.translations || {};
                const translatedName = translations[currentLang]?.name || product.name;
                const translatedDesc = translations[currentLang]?.description || product.description;
                
                // Helper to get country flag emoji
                const getCountryFlag = (countryCode) => {
                  if (!countryCode) return null;
                  const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt());
                  return String.fromCodePoint(...codePoints);
                };
                
                // Get badge settings from admin panel
                const productBadge = homepageSettings?.product_badges?.[product.id] || {};
                const showFlag = productBadge.country_flag;
                const showMadeInTurkey = productBadge.made_in_turkey;
                
                // Optimize image URL for mobile (/uploads + CDN boyutları)
                const rawImg = homeProductImage(product);
                let displayImg = rawImg.startsWith('/uploads/')
                  ? rawImg.replace('/uploads/', '/api/uploads/')
                  : rawImg;
                if (displayImg && !displayImg.includes('w=')) {
                  if (displayImg.includes('unsplash.com')) {
                    displayImg = displayImg + (displayImg.includes('?') ? '&' : '?') + 'w=200&q=70';
                  } else if (displayImg.includes('pexels.com')) {
                    displayImg = displayImg + (displayImg.includes('?') ? '&' : '?') + 'w=200';
                  }
                }
                
                return (
                <Link 
                  key={product.id} 
                  to={buildProductPath(langCode, product)}
                  className="bg-white rounded-lg sm:rounded-xl p-1.5 sm:p-2 shadow-md hover:shadow-xl transition-all hover:-translate-y-1 group"
                >
                  <div className="relative aspect-square mb-1 sm:mb-1.5 overflow-hidden rounded-md sm:rounded-lg bg-gray-100">
                    <ProductThumbnail
                      src={displayImg}
                      alt={translatedName}
                      className="w-full h-full"
                      coverImgClassName="group-hover:scale-105 transition-all duration-300"
                      loading="lazy"
                      sensitive={shouldBlurSensitiveImage(product)}
                      onUnavailable={() => hideBrokenProduct(product.id)}
                    />
                    
                    {/* Admin tarafından eklenen bayrak ve Made in Turkey rozetleri */}
                    <div className="absolute top-0.5 sm:top-1 left-0.5 sm:left-1 flex flex-col gap-0.5 z-10">
                      {showFlag && (
                        <span className="text-sm sm:text-lg drop-shadow-md" title={showFlag}>
                          {getCountryFlag(showFlag)}
                        </span>
                      )}
                      {showMadeInTurkey && (
                        <img 
                          src="https://customer-assets.emergentagent.com/job_emarket-turkey-1/artifacts/dupde9ke_madeinturkiye%20logo%20.png"
                          alt="Made in Türkiye"
                          className="w-5 h-5 sm:w-6 sm:h-6 object-contain drop-shadow-md"
                          title="Made in Türkiye"
                        />
                      )}
                    </div>
                    
                    {/* Heart Button - Configurable */}
                    {(homepageSettings?.flash_deals_show_heart_button !== false) && (
                      <button 
                        className={`absolute top-0.5 sm:top-1 right-0.5 sm:right-1 w-5 sm:w-6 h-5 sm:h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm ${isInWishlist(product.id) ? 'bg-red-500' : 'bg-white/90'}`}
                        onClick={(e) => handleWishlistToggle(e, product)}
                        data-testid={`homepage-wishlist-${product.id}`}
                      >
                        <Heart className={`w-2.5 sm:w-3 h-2.5 sm:h-3 ${isInWishlist(product.id) ? 'text-white fill-white' : 'text-gray-600 hover:text-red-500'}`} />
                      </button>
                    )}
                    
                    {/* Fallback: Satıcı ülke bayrağı (admin ayarı yoksa) */}
                    {!showFlag && product.seller_info?.country && (
                      <div className="absolute bottom-0.5 sm:bottom-1 right-0.5 sm:right-1 bg-white/90 backdrop-blur-sm px-1 py-0.5 rounded text-[10px] sm:text-xs shadow-sm">
                        {getCountryFlag(product.seller_info.country)}
                      </div>
                    )}
                  </div>
                  <h3 className="text-[10px] sm:text-xs font-medium text-gray-900 line-clamp-1 mb-0.5">{translatedName}</h3>
                  <div className="flex items-center gap-1">
                    <span className="text-red-600 font-bold text-[10px] sm:text-xs">{formatPrice(product.price, product.currency || 'EUR')}</span>
                  </div>
                  <div className="flex items-center gap-0.5 mt-0.5">
                    <Star className="w-2 sm:w-2.5 h-2 sm:h-2.5 fill-yellow-400 text-yellow-400" />
                    <span className="text-[8px] sm:text-[10px] text-gray-500">{product.rating || (Math.random() * 2 + 3).toFixed(1)}</span>
                  </div>
                </Link>
              );})}
            </div>
          )}

          {/* View All Button */}
          <div className="text-center mt-4">
            <Link to={`/${langCode}/products?featured=true`}>
              <Button className="bg-red-600 text-white hover:bg-red-700 rounded-full px-8 font-semibold shadow-lg">
                {t('common.viewAll')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      {homepageSettings?.services_enabled !== false && homeServicesGridItems.length > 0 ? (
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-xl font-bold mb-4">
            {langCode === 'tr'
              ? (homepageSettings?.services_title || t('services.ourServices'))
              : t('services.ourServices')}
          </h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            {homeServicesGridItems.map((service) => (
              <Link
                key={service.id}
                to={
                  service.categorySlug
                    ? buildPathForLocale(langCode, ['category', service.categorySlug])
                    : localizeInternalPath(service.link, langCode)
                }
                className="relative h-40 rounded-xl overflow-hidden group hover:scale-[1.02] transition-transform"
                data-testid={`service-card-${service.id}`}
              >
                {/* Background Image */}
                <img 
                  src={service.image} 
                  alt={service.name}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-75 group-hover:opacity-85 transition-opacity`} />
                {/* Content */}
                <div className="absolute inset-0 p-4 flex flex-col justify-between">
                  <service.icon className="w-8 h-8 text-white/90" />
                  <div>
                    <h3 className="text-white font-bold text-sm">{service.name}</h3>
                    <p className="text-white/80 text-xs">{service.desc}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      ) : null}

      {/* All Products Grid - Sizin İçin Önerilenler - 8 sütun x 4 satır = 32 ürün */}
      {/* Tüm satıcılardan karışık, her üründen sadece 1 adet */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">{t('home.recommendedForYou')}</h2>
            <Link to="/products" className="text-sm text-primary hover:underline flex items-center gap-1">
              {t('common.viewAll')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
              {[...Array(32)].map((_, i) => (
                <div key={i} className="space-y-1">
                  <Skeleton className="aspect-square rounded-lg" />
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="all-products">
              {recommendedRows.map((product) => {
                const badge = homepageSettings?.product_badges?.[product.id];
                const withBadge = badge
                  ? { ...product, country_flag: badge.country_flag, made_in_turkey: badge.made_in_turkey }
                  : product;
                return (
                  <ProductCard
                    key={product.id}
                    product={withBadge}
                    compact
                    onImageError={() => hideBrokenProduct(product.id)}
                  />
                );
              })}
            </div>
          )}

          <div className="text-center mt-6">
            <Link to="/products">
              <Button className="rounded-full px-8">
                {t('home.loadMore') || 'Daha Fazla Göster'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="py-6 sm:py-8 bg-gray-100">
        <div className="container mx-auto px-3 sm:px-4">
          {/* Mobile: 2x2 grid, Desktop: 4 columns */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            {[
              { icon: Shield, titleKey: 'features.securePayment', descKey: 'features.ssl256', action: null },
              { icon: CreditCard, titleKey: 'features.easyReturn', descKey: 'features.within14Days', action: null },
              { icon: Truck, titleKey: 'features.fastShipping', descKey: 'features.sameDayShipping', action: null },
              { icon: Headphones, titleKey: 'features.support247', descKey: 'features.chatSupport', action: 'openChat' },
            ].map((feature, index) => (
              <div 
                key={index} 
                className={`flex items-center gap-2 sm:gap-3 bg-white p-2.5 sm:p-4 rounded-xl ${
                  feature.action === 'openChat' ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''
                }`}
                onClick={() => {
                  if (feature.action === 'openChat') {
                    const chatBtn = document.querySelector('[data-testid="chat-widget-button"]');
                    if (chatBtn) chatBtn.click();
                  }
                }}
              >
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-xs sm:text-sm text-gray-900">{t(feature.titleKey)}</h4>
                  <p className="text-[10px] sm:text-xs text-gray-500">{t(feature.descKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
